// ─── Month calendar ──────────────────────────────────────────────────────────
//
// The business-side month view, and the mobile month view, from one component.
// The design doc is explicit that desktop and mobile are "the same booking
// records and availability engine, never separate booking products" — so they
// are the same component with a different density, not two implementations
// that will drift.
//
// ── What it does not do ─────────────────────────────────────────────────────
//
// No data fetching, no dates library, no knowledge of bookings. It takes days
// with facts attached and renders them. That keeps it usable by the business
// calendar, the customer booking page and any later product, and it means the
// tests need no database.

import { useMemo } from "react";
import { dayState, dayStateLabel, volumeDots, availabilityDots, type DayFacts } from "./dayState.js";

export type MonthDay = DayFacts & {
  /** The date this tile represents. */
  date: Date;
  /** False for the leading/trailing days that pad the grid. */
  inMonth: boolean;
};

export type MonthCalendarProps = {
  /** Days in grid order, Monday first. Supply a whole number of weeks. */
  days: MonthDay[];
  selected?: Date | null;
  /** A single click or tap. Selects the day — the cheap, reversible action. */
  onSelect?: (date: Date) => void;
  /**
   * A double click or double tap. Opens the day properly.
   *
   * ── Why single-click is not delayed to wait for a second one ─────────────
   *
   * The textbook way to tell a single click from a double is to hold the
   * single action for ~250ms and cancel it if a second click arrives. That
   * makes EVERY selection feel slow to buy correctness for the rarer
   * gesture — and selecting a day is the thing she will do all day long.
   *
   * So both fire: a double click selects the day and then opens it. The
   * summary appears for a moment first, which is a fair price for a month
   * grid that responds instantly to the common action.
   *
   * Double-tap is also unreliable as the ONLY route on a phone, so whatever
   * renders the summary should offer its own way in as well.
   */
  onOpenDay?: (date: Date) => void;
  /** `business` shows how busy each day is; `customer` shows how much is
   *  free. Same component, different data — a customer must not be shown the
   *  business's booking volume. */
  surface?: "business" | "customer";
  density?: "comfortable" | "compact";
  monthLabel: string;
};

/** Monday first — the documented default for every calendar in this product. */
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export function MonthCalendar({
  days, selected, onSelect, onOpenDay, surface = "business", density = "comfortable", monthLabel,
}: MonthCalendarProps) {
  const weeks = useMemo(() => {
    const out: MonthDay[][] = [];
    for (let i = 0; i < days.length; i += 7) out.push(days.slice(i, i + 7));
    return out;
  }, [days]);

  const compact = density === "compact";

  return (
    <div data-testid="month-calendar" style={{ display: "flex", flexDirection: "column", gap: "var(--nl-tile-gap)" }}>
      <div
        style={{
          display: "grid", gridTemplateColumns: "repeat(7, 1fr)",
          gap: "var(--nl-tile-gap)", paddingBottom: 2,
        }}
      >
        {WEEKDAYS.map(d => (
          <div
            key={d}
            aria-hidden="true"
            style={{
              textAlign: "center", fontSize: compact ? "0.68rem" : "0.75rem",
              letterSpacing: "0.06em", textTransform: "uppercase",
              color: "var(--nl-ink-faint)",
            }}
          >{compact ? d[0] : d}</div>
        ))}
      </div>

      {weeks.map((week, wi) => (
        <div
          key={wi}
          role="row"
          style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "var(--nl-tile-gap)" }}
        >
          {week.map(day => {
            const isSelected = !!selected && sameDay(day.date, selected);
            const state = dayState({ ...day, selected: isSelected });
            // The surface decides the words as well as the dots — a customer
            // must not be told how many bookings the business is holding.
            const label = dayStateLabel(state, day, surface);
            const dots = surface === "business" ? volumeDots(day.bookings) : availabilityDots(day.openSlots);

            return (
              <button
                key={day.date.toISOString()}
                type="button"
                role="gridcell"
                aria-current={isSelected ? "date" : undefined}
                aria-label={`${day.date.toDateString()} — ${label}`}
                data-state={state}
                data-in-month={day.inMonth}
                onClick={() => onSelect?.(day.date)}
                onDoubleClick={() => onOpenDay?.(day.date)}
                // Deliberately NO keyboard handler for "open".
                //
                // A <button> already fires onClick on Enter, so binding Enter
                // to open as well would mean a keyboard user could only ever
                // open a day and never simply select one — the reverse of the
                // mouse behaviour. There is no natural keyboard double-click.
                //
                // The accessible route to the full day is a real button in the
                // summary panel, which is also the reliable route on a phone,
                // where double-tap is a fragile gesture. Double-click is a
                // shortcut on top of that, never the only way in.
                style={{
                  // Absolute radius, never a percentage or 9999px: a tile
                  // wider than it is tall must still read as a rectangle.
                  borderRadius: "var(--nl-radius-tile)",
                  border: "1px solid var(--nl-stroke-soft)",
                  background: state === "selected" ? "var(--nl-state-selected)" : `var(--nl-state-${state})`,
                  color: state === "selected" ? "var(--nl-state-selected-ink)" : `var(--nl-state-${state}-ink)`,
                  minHeight: compact ? "var(--nl-hit-min)" : "5.5rem",
                  padding: compact ? "6px 4px" : "8px 10px",
                  display: "flex", flexDirection: "column",
                  alignItems: compact ? "center" : "flex-start",
                  justifyContent: compact ? "center" : "flex-start",
                  gap: 4,
                  cursor: onSelect || onOpenDay ? "pointer" : "default",
                  opacity: day.inMonth ? 1 : 0.45,
                  font: "inherit", textAlign: "left",
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--nl-font-num)",
                    fontVariantNumeric: "tabular-nums",
                    fontSize: compact ? "0.9rem" : "1.05rem",
                    fontWeight: isSelected ? 650 : 500,
                  }}
                >{day.date.getDate()}</span>

                {/* Dots are generated from real data, never decorative. */}
                {dots > 0 && (
                  <span style={{ display: "flex", gap: 3 }} aria-hidden="true">
                    {Array.from({ length: dots }, (_, i) => (
                      <span
                        key={i}
                        style={{
                          width: 5, height: 5, borderRadius: 2,
                          background: "currentColor", opacity: 0.75,
                        }}
                      />
                    ))}
                  </span>
                )}

                {/* The non-colour half of the state language. Hidden on the
                    compact layout for room, but the accessible name on the
                    button always carries it. */}
                {!compact && (
                  <span style={{ fontSize: "0.7rem", opacity: 0.85, marginTop: "auto" }}>{label}</span>
                )}
              </button>
            );
          })}
        </div>
      ))}

      <div style={{ fontSize: "0.75rem", color: "var(--nl-ink-faint)", paddingTop: 2 }}>{monthLabel}</div>
    </div>
  );
}
